import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, Collapse, List, Space, Button, Badge, Empty, Spin, Typography, Tooltip, Modal, Input, message } from 'antd';
import { ClockCircleOutlined, ReloadOutlined, CheckOutlined, RightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { API_BASE_URL } from '../../utils/config';
import { formatMoney } from '../../utils/currency';

const { Text } = Typography;

const formatTL = (v) => formatMoney(v || 0);

const formatDate = (v) => {
  if (!v) return '';
  const d = dayjs(v);
  return d.isValid() ? d.format('DD.MM.YYYY') : String(v);
};

export default function PendingTasksWidget() {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();

  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [gorevler, setGorevler] = useState([]);
  const [hakedisler, setHakedisler] = useState([]);
  const [odemeTalepleri, setOdemeTalepleri] = useState([]);
  const [sayilar, setSayilar] = useState({ gorev: 0, hakedis: 0, odeme: 0 });
  const [tamamModal, setTamamModal] = useState(null);
  const [tamamAciklama, setTamamAciklama] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const res = await axios.get(`${API_BASE_URL}/surec/my-pending`, {
        headers: getAuthHeaders()
      });
      const d = res.data?.data || res.data || {};
      setGorevler(Array.isArray(d.gorevler) ? d.gorevler : []);
      setHakedisler(Array.isArray(d.hakedisler) ? d.hakedisler : []);
      setOdemeTalepleri(Array.isArray(d.odeme_talepleri) ? d.odeme_talepleri : []);
      setSayilar({
        gorev: Number(d.sayilar?.gorev || 0),
        hakedis: Number(d.sayilar?.hakedis || 0),
        odeme: Number(d.sayilar?.odeme || 0)
      });
    } catch (error) {
      console.warn('PendingTasksWidget: süreç verisi alınamadı', error);
      setFailed(true);
      setGorevler([]);
      setHakedisler([]);
      setOdemeTalepleri([]);
      setSayilar({ gorev: 0, hakedis: 0, odeme: 0 });
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tamamlaGorev = async () => {
    if (!tamamModal) return;
    try {
      await axios.post(
        `${API_BASE_URL}/surec/gorev/${tamamModal.id}/tamamla`,
        { aciklama: tamamAciklama || null },
        { headers: getAuthHeaders() }
      );
      message.success('Görev tamamlandı');
    } catch (error) {
      console.warn('PendingTasksWidget: görev tamamlanamadı', error);
      message.error('Görev tamamlanamadı');
    }
    setTamamModal(null);
    setTamamAciklama('');
    loadData();
  };

  const total =
    Number(sayilar.gorev || 0) +
    Number(sayilar.hakedis || 0) +
    Number(sayilar.odeme || 0);

  const allEmpty =
    hakedisler.length === 0 && gorevler.length === 0 && odemeTalepleri.length === 0;

  const collapseItems = [];

  if (hakedisler.length > 0) {
    collapseItems.push({
      key: 'hakedis',
      label: `Onay Bekleyen Hakedişler (${hakedisler.length})`,
      children: (
        <List
          size="small"
          dataSource={hakedisler}
          renderItem={(h) => (
            <List.Item
              actions={[
                <Button
                  key="git"
                  size="small"
                  icon={<RightOutlined />}
                  onClick={() => navigate('/finans')}
                >
                  Git
                </Button>
              ]}
            >
              <Text style={{ fontSize: 13 }}>
                #{h.id} · {h.cari_ad} · {formatTL(h.odenecek_tutar)} · adım: {h.onay_adim}
              </Text>
            </List.Item>
          )}
        />
      )
    });
  }

  if (gorevler.length > 0) {
    collapseItems.push({
      key: 'gorev',
      label: `Görevlerim (${gorevler.length})`,
      children: (
        <List
          size="small"
          dataSource={gorevler}
          renderItem={(g) => (
            <List.Item
              actions={[
                <Button
                  key="git"
                  size="small"
                  onClick={() => navigate(g.url || '/home')}
                >
                  Git
                </Button>,
                // İzin (Yer Sahibi / Belediye) görevleri Onay/Red ister — karar Süreç İzleme'de verilir
                ...(['yer_sahibi_onay', 'belediye_onay'].includes(g.tip) ? [] : [
                  <Tooltip key="tamamla" title="Tamamla / Açıklama gir">
                    <Button
                      size="small"
                      type="primary"
                      ghost
                      icon={<CheckOutlined />}
                      onClick={() => { setTamamModal(g); setTamamAciklama(''); }}
                    >
                      Tamamla
                    </Button>
                  </Tooltip>
                ])
              ]}
            >
              <Space direction="vertical" size={0}>
                <Text style={{ fontSize: 13 }}>{g.baslik}</Text>
                {g.tarih && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {formatDate(g.tarih)}
                  </Text>
                )}
              </Space>
            </List.Item>
          )}
        />
      )
    });
  }

  if (odemeTalepleri.length > 0) {
    collapseItems.push({
      key: 'odeme',
      label: `Bekleyen Ödeme Talepleri (${odemeTalepleri.length})`,
      children: (
        <List
          size="small"
          dataSource={odemeTalepleri}
          renderItem={(o) => (
            <List.Item
              actions={[
                <Button
                  key="git"
                  size="small"
                  icon={<RightOutlined />}
                  onClick={() => navigate('/finans')}
                >
                  Git
                </Button>
              ]}
            >
              <Text style={{ fontSize: 13 }}>
                #{o.id} · {o.cari_ad} · {formatTL(o.tutar)} · vade {formatDate(o.vade)}
              </Text>
            </List.Item>
          )}
        />
      )
    });
  }

  return (
   <>
    <Card
      style={{ marginBottom: 16 }}
      size="small"
      title={
        <Space>
          <ClockCircleOutlined />
          <span>Bekleyen Süreçlerim</span>
          <Badge count={total} showZero={false} overflowCount={999} />
        </Space>
      }
      extra={
        <Button
          size="small"
          icon={<ReloadOutlined />}
          loading={loading}
          onClick={loadData}
        >
          Yenile
        </Button>
      }
    >
      {loading && allEmpty ? (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <Spin size="small" />
        </div>
      ) : allEmpty ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={failed ? 'Süreç verisi alınamadı' : 'Bekleyen işlem yok 🎉'}
        />
      ) : (
        <div style={{ maxHeight: '55vh', overflowY: 'auto', overflowX: 'hidden' }}>
          <Collapse
            size="small"
            items={collapseItems}
            defaultActiveKey={collapseItems.map((i) => i.key)}
          />
        </div>
      )}
    </Card>

    <Modal
      title="Görevi Tamamla"
      open={!!tamamModal}
      onOk={tamamlaGorev}
      onCancel={() => { setTamamModal(null); setTamamAciklama(''); }}
      okText="Tamamla"
      cancelText="Vazgeç"
      destroyOnClose
    >
      {tamamModal && (
        <div style={{ marginBottom: 8 }}>
          <Text>{tamamModal.baslik}</Text>
        </div>
      )}
      <Input.TextArea
        rows={3}
        placeholder="Açıklama / not (opsiyonel) — örn. saha planı, yapılan iş..."
        value={tamamAciklama}
        onChange={(e) => setTamamAciklama(e.target.value)}
      />
    </Modal>
   </>
  );
}
